import React, { useContext } from "react";
import { saveAs } from "file-saver";
import { usePartnersList } from "@/hooks";
import { Button } from "@mui/material";
import { useLang, useTextDomainContext } from "@/app/providers";
import { palette as AbsoluteSightPalette } from "@/app/providers/MaterialThemeProvider/absolute-sight/palette";
import { palette as FabekePalette } from "@/app/providers/MaterialThemeProvider/fabeke/palette";
import { palette as EnergiteamPalette } from "@/app/providers/MaterialThemeProvider/energiteam/palette";
import { palette as SustainorPalette } from "@/app/providers/MaterialThemeProvider/sustainor/palette";
import {
  emailConfirmEmail,
  emailFooter,
  emailForgotPassword,
  emailHeader,
  emailInviteUserToPartnerExisting,
  emailInviteUserToPartnerNew,
  emailInviteUserToTenantExisting,
  emailInviteUserToTenantNew,
  emailSendStatementReport,
} from "./components";
import { languageList, sleep } from "@/utils";

type EmailTemplate = {
  code: string;
  name: string;
};

type EmailPartner = {
  key: string;
  name: string;
  appUrl: string;
};

const emailTemplates: EmailTemplate[] = [
  {
    code: "confirm-email",
    name: "Confirm Email",
  },
  {
    code: "forget-password",
    name: "Forgot Password",
  },
  {
    code: "invite-user-to-tenant-new",
    name: "Invite New User to the corporate group",
  },
  {
    code: "invite-user-to-tenant-existing",
    name: "Invite Existing User to the corporate group",
  },
  {
    code: "invite-user-to-partner-new",
    name: "Invite New User to Partner",
  },
  {
    code: "invite-user-to-partner-existing",
    name: "Invite Existing User to Partner",
  },
  {
    code: "send-statement-report",
    name: "Send Statement Report",
  },
];

const colors: Record<string, string> = {
  "absolute-sight": AbsoluteSightPalette.primary.main,
  fabeke: FabekePalette.primary.main,
  energiteam: EnergiteamPalette.primary.main,
  sustainor: SustainorPalette.primary.main,
};

export const DeveloperGenerateEmail = () => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const languages = languageList(gettext);
  const locale = useLang();
  const { partnerList } = usePartnersList() as { partnerList: EmailPartner[] };
  const buttonList: React.ReactElement[] = [];

  const selectedLanguageObj = languages.find((row) => row.lang === locale);

  emailTemplates.forEach((template, index1) => {
    buttonList.push(<h2 key={"h2" + index1}>{template.name}</h2>);
    partnerList.forEach((partner, index2) => {
      buttonList.push(
        <Button
          key={index1 + "-" + index2}
          variant="contained"
          // color="primary"
          onClick={(e) => saveEmail(e, template, partner)}
          sx={{
            m: 1,
            backgroundColor: colors[partner.key],
          }}
        >
          {template.name}: {partner.name}
        </Button>,
      );
    });
    buttonList.push(<br key={"br1" + index1} />);
    buttonList.push(<br key={"br2" + index1} />);
  });

  const emailContent = (template: EmailTemplate, partner: EmailPartner) => {
    let returnHtml = emailHeader(gettext, locale, partner, colors);
    switch (template.code) {
      case "confirm-email":
        returnHtml += emailConfirmEmail(gettext);
        break;
      case "forget-password":
        returnHtml += emailForgotPassword(gettext);
        break;
      case "invite-user-to-tenant-new":
        returnHtml += emailInviteUserToTenantNew(gettext);
        break;
      case "invite-user-to-tenant-existing":
        returnHtml += emailInviteUserToTenantExisting(gettext, partner);
        break;
      case "invite-user-to-partner-new":
        returnHtml += emailInviteUserToPartnerNew(gettext);
        break;
      case "invite-user-to-partner-existing":
        returnHtml += emailInviteUserToPartnerExisting(gettext, partner);
        break;
      case "send-statement-report":
        returnHtml += emailSendStatementReport(gettext, partner);
        break;
      default:
        returnHtml = "";
        break;
    }
    returnHtml += emailFooter(gettext);
    return returnHtml;
  };

  const saveEmail = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    template: EmailTemplate,
    partner: EmailPartner,
  ) => {
    void event;
    const filename = `${template.code}-${partner.key}-${locale}`;
    const emailText = emailContent(template, partner);
    const blob = new Blob([emailText], {
      type: "text/html;charset=utf-8",
    });
    saveAs(blob, `${filename}.html`);
  };

  const saveAllEmails = async () => {
    for (const template of emailTemplates) {
      for (const partner of partnerList) {
        await saveEmail(null, template, partner);
        await sleep(500);
      }
    }
  };

  return (
    <>
      <Button
        key="downlaod-all"
        variant="contained"
        onClick={saveAllEmails}
        sx={{
          m: 1,
          color: "primary",
        }}
      >
        {gettext("Save All For") +
          " " +
          selectedLanguageObj?.name +
          " " +
          gettext("Language")}
      </Button>
      <br />
      <br />
      {buttonList}
    </>
  );
};
